import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export interface PlanLimitCheck {
  permitido: boolean;
  uso_atual: number;
  limite: number | null;
}

type AppRole = Database['public']['Enums']['app_role'];

export interface Organization {
  id: string;
  owner_id: string;
  nome: string;
  plano: string;
  limites: Record<string, number | null> | null;
  setor: string | null;
  pais: string | null;
  subscription_status: string | null;
  logo_url: string | null;
  descricao_publica: string | null;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: AppRole;
  ativo: boolean;
}

/**
 * Checks whether a given resource type (e.g. 'prestadores', 'ordens_mes')
 * is within the organization's current plan limits.
 * Delegates to the `verificar_limite` RPC, which is the single source of
 * truth for soft/hard-limit enforcement across the app.
 */
export async function checkPlanLimit(params: {
  orgId: string;
  tipo: string;
}): Promise<PlanLimitCheck> {
  const { data, error } = await supabase.rpc('verificar_limite', {
    p_org_id: params.orgId,
    p_tipo: params.tipo,
  });

  if (error) throw error;
  return data as PlanLimitCheck;
}

/**
 * Fetches the organization_members row for a given user.
 * Returns null if no membership exists (user hasn't completed onboarding).
 * Throws on other Supabase errors.
 */
export async function getMembershipByUserId(userId: string): Promise<OrganizationMember | null> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('id, organization_id, user_id, role, ativo')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as OrganizationMember;
}

/**
 * Fetches an organization by id.
 * Returns null if the organization does not exist.
 * Throws on other Supabase errors.
 */
export async function getOrganizationById(orgId: string): Promise<Organization | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('id, owner_id, nome, plano, limites, setor, pais, subscription_status, logo_url, descricao_publica')
    .eq('id', orgId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return {
    id: data.id,
    owner_id: data.owner_id,
    nome: data.nome,
    plano: data.plano,
    limites: data.limites as Record<string, number | null> | null,
    setor: data.setor,
    pais: data.pais,
    subscription_status: data.subscription_status,
    logo_url: data.logo_url,
    descricao_publica: data.descricao_publica,
  };
}

const LOGO_BUCKET = 'org-logos';

/**
 * Faz upload do logo da organização para o bucket público `org-logos`
 * (caminho `{orgId}/logo-{timestamp}.{ext}`) e devolve o URL público.
 */
export async function uploadOrgLogo(params: { orgId: string; file: File }): Promise<string> {
  const ext = params.file.name.split('.').pop()?.toLowerCase() || 'png';
  const path = `${params.orgId}/logo-${Date.now()}.${ext}`;

  // Remove logos antigos da pasta da org antes de gravar o novo, para não
  // acumular ficheiros órfãos (cada upload usa um nome com timestamp).
  const { data: antigos } = await supabase.storage.from(LOGO_BUCKET).list(params.orgId);
  if (antigos && antigos.length > 0) {
    await supabase.storage
      .from(LOGO_BUCKET)
      .remove(antigos.map((f) => `${params.orgId}/${f.name}`));
  }

  const { error } = await supabase.storage
    .from(LOGO_BUCKET)
    .upload(path, params.file, { upsert: true, contentType: params.file.type });
  if (error) throw error;

  const { data } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Grava (ou limpa, com null) o logo_url da organização do utilizador
 * autenticado, via RPC security-definer que valida admin/owner.
 */
export async function setOrgLogoUrl(params: { logoUrl: string | null }): Promise<void> {
  const { error } = await supabase.rpc('definir_logo_organizacao', { p_logo_url: params.logoUrl });
  if (error) throw error;
}

/**
 * Remove o logo: apaga o objeto do storage (se o path for derivável a partir
 * do URL público) e limpa o logo_url.
 */
export async function removeOrgLogo(params: { orgId: string; logoUrl: string | null }): Promise<void> {
  if (params.logoUrl) {
    const marker = `/${LOGO_BUCKET}/`;
    const idx = params.logoUrl.indexOf(marker);
    if (idx !== -1) {
      const path = params.logoUrl.slice(idx + marker.length);
      const { error } = await supabase.storage.from(LOGO_BUCKET).remove([path]);
      if (error) throw error;
    }
  }
  await setOrgLogoUrl({ logoUrl: null });
}

/**
 * Desativa um membro da organização (reversível). Valida admin/owner,
 * guardrails e efeitos colaterais no servidor (RPC security-definer).
 * Faz throw do erro cru — o hook traduz via parseDesativarError.
 */
export async function desativarMembro(params: { userId: string }): Promise<void> {
  const { error } = await supabase.rpc('desativar_membro', { p_user_id: params.userId });
  if (error) throw error;
}

/** Reativa um membro previamente desativado (respeita o teto do plano). */
export async function reativarMembro(params: { userId: string }): Promise<void> {
  const { error } = await supabase.rpc('reativar_membro', { p_user_id: params.userId });
  if (error) throw error;
}
